// FILE: src/admin/pages/directory/DirectoryOwners.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { fetchDirectoryOwners } from "../../../api/adminDirectoryApi";

/* --------------------- helpers --------------------- */

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function safeStr(x) {
  return x == null ? "" : String(x);
}
function cleanStr(x) {
  const s = safeStr(x).trim();
  return s ? s : "";
}
function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function moneyINR(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "₹0";
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}
function pct01(x, digits = 0) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}
function dtClean(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}
function useDebouncedValue(value, delayMs = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

function labelOwner(x) {
  const id = cleanStr(x?.ownerId ?? x?.id);
  const name = cleanStr(x?.ownerName ?? x?.name);
  const phone = cleanStr(x?.ownerPhone ?? x?.phone);
  const left = name || `Owner #${id || "—"}`;
  return phone ? `${left} • ${phone}` : left;
}

/* --------------------- UI primitives --------------------- */

function Chip({ tone = "slate", children }) {
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "red"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : tone === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : tone === "blue"
      ? "bg-sky-50 text-sky-900 border-sky-200"
      : "bg-slate-50 text-slate-800 border-slate-200";

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold leading-none whitespace-nowrap",
        cls
      )}
    >
      {children}
    </span>
  );
}

/** KPI card – enforced consistent height across the grid */
function KpiCard({ title, value, hint, accent = "slate" }) {
  const ring =
    accent === "green"
      ? "ring-emerald-100"
      : accent === "red"
      ? "ring-rose-100"
      : accent === "amber"
      ? "ring-amber-100"
      : accent === "blue"
      ? "ring-sky-100"
      : "ring-slate-100";

  const dot =
    accent === "green"
      ? "bg-emerald-500"
      : accent === "red"
      ? "bg-rose-500"
      : accent === "amber"
      ? "bg-amber-500"
      : accent === "blue"
      ? "bg-sky-500"
      : "bg-slate-700";

  return (
    <div
      className={classNames(
        "rounded-xl border border-slate-200 bg-white px-4 py-3 ring-4 h-[96px] flex flex-col justify-between",
        ring
      )}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap truncate">
          {title}
        </div>
        <span className={classNames("h-2 w-2 rounded-full shrink-0", dot)} aria-hidden="true" />
      </div>

      <div className="mt-1 text-2xl font-semibold text-slate-900 whitespace-nowrap truncate">{value}</div>

      {hint ? (
        <div className="mt-1 text-xs text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis truncate">
          {hint}
        </div>
      ) : (
        <div className="mt-1 text-xs text-slate-500 whitespace-nowrap opacity-0">.</div>
      )}
    </div>
  );
}

/* --------- cleaner, color-coded meters --------- */

function meterClasses(tone) {
  switch (tone) {
    case "blue":
      return { track: "bg-sky-100", bar: "bg-gradient-to-r from-sky-600 to-sky-500" };
    case "green":
      return { track: "bg-emerald-100", bar: "bg-gradient-to-r from-emerald-600 to-emerald-500" };
    case "red":
      return { track: "bg-rose-100", bar: "bg-gradient-to-r from-rose-600 to-rose-500" };
    case "amber":
      return { track: "bg-amber-100", bar: "bg-gradient-to-r from-amber-600 to-amber-500" };
    default:
      return { track: "bg-slate-100", bar: "bg-gradient-to-r from-slate-700 to-slate-600" };
  }
}

function Meter({ value01, tone = "slate" }) {
  const w = clamp(Math.round(num(value01) * 100), 0, 100);
  const { track, bar } = meterClasses(tone);

  return (
    <div className={classNames("mt-2 h-2.5 w-full rounded-full overflow-hidden", track)}>
      <div className={classNames("h-full rounded-full shadow-sm", bar)} style={{ width: `${w}%` }} />
    </div>
  );
}

/** Top lists – cleaner row + color-coded meter */
function SimpleBarList({ items = [], valueKey = "value", rightRenderer, tone = "blue" }) {
  const maxV = Math.max(1, ...items.map((x) => num(x?.[valueKey])));

  return (
    <div className="space-y-2">
      {items.map((x, idx) => {
        const v = num(x?.[valueKey]);
        const value01 = v / maxV;

        return (
          <div key={idx} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-bold text-slate-700">
                  {idx + 1}
                </span>
                <div className="text-sm font-semibold text-slate-900 truncate">{labelOwner(x)}</div>
              </div>

              <div className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                {rightRenderer ? rightRenderer(x) : v.toLocaleString("en-IN")}
              </div>
            </div>

            <Meter value01={value01} tone={tone} />
          </div>
        );
      })}

      {items.length === 0 ? <div className="text-sm text-slate-500">No data.</div> : null}
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
          {subtitle ? <div className="mt-1 text-sm text-slate-600">{subtitle}</div> : null}
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

/* ✅ Your exact pagination footer style */
function PaginationFooter({ page, size, totalPages, totalItems, onPrev, onNext, onSize }) {
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500 whitespace-nowrap">
          Showing <span className="font-semibold text-slate-700">{size}</span> • Total{" "}
          <span className="font-semibold text-slate-700">{totalItems}</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500 whitespace-nowrap">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => onSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">▼</span>
            </div>
          </div>

          <div className="text-sm text-slate-700 whitespace-nowrap">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={!canPrev}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={!canNext}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------- CSV export --------------------- */

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}
function downloadTextFile(filename, text, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* --------------------- payout helpers --------------------- */

function payoutTone(st) {
  const s = safeStr(st).toUpperCase();
  if (s === "VERIFIED") return "green";
  if (s === "ACTION_REQUIRED") return "red";
  if (s === "SUBMITTED") return "amber";
  if (s === "DRAFT") return "slate";
  return "slate";
}
function payoutLabel(st) {
  const s = safeStr(st).toUpperCase();
  if (!s) return "DRAFT";
  return s.replaceAll("_", " ");
}

/* --------------------- page --------------------- */

export default function DirectoryOwners() {
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  const [status, setStatus] = useState("ALL"); // payout account status
  const [risk, setRisk] = useState("ALL"); // ATTENTION only
  const [link, setLink] = useState("ALL"); // LINKED/UNLINKED

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [resp, setResp] = useState(null);

  const fetchSeq = useRef(0);

  useEffect(() => {
    setPage(0);
  }, [qDebounced, status, risk, link, size]);

  useEffect(() => {
    let alive = true;
    const seq = ++fetchSeq.current;

    async function run() {
      setLoading(true);
      setErr("");

      try {
        const data = await fetchDirectoryOwners({
          q: qDebounced,
          status: status === "ALL" ? undefined : status,
          risk: risk === "ALL" ? undefined : risk,
          link: link === "ALL" ? undefined : link,
          page,
          size,
        });

        if (!alive || seq !== fetchSeq.current) return;
        setResp(data || null);
      } catch (e) {
        if (!alive || seq !== fetchSeq.current) return;
        setErr(e?.message || "Failed to load directory owners");
        setResp(null);
      } finally {
        if (!alive || seq !== fetchSeq.current) return;
        setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [qDebounced, status, risk, link, page, size]);

  const summary = resp?.summary || null;
  const items = resp?.items || [];
  const totalItems = num(resp?.totalItems);
  const totalPages = num(resp?.totalPages);
  const currentPage = num(resp?.page);
  const pageSize = num(resp?.size);

  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 19).replaceAll(":", "-");
    const filename = `owners-directory-page-${currentPage + 1}-${stamp}.csv`;

    const header = [
      "OwnerId",
      "Name",
      "Phone",
      "Location",
      "PayoutStatus",
      "PayoutMethod",
      "Vehicles",
      "VerifiedVehicles",
      "ActionRequiredVehicles",
      "PendingReviewVehicles",
      "Bookings",
      "Rides",
      "CancelCount",
      "CancelRate",
      "LifetimeFare",
      "LifetimePayoutSuccess",
      "PendingPayoutTxnCount",
      "PendingPayoutAmount",
      "LastActivityAt",
      "AttentionNeeded",
    ];

    const lines = [header.map(csvEscape).join(",")];

    items.forEach((o) => {
      lines.push(
        [
          o.id,
          o.name,
          o.phone,
          o.location ?? "",
          o.payoutAccountStatus,
          o.payoutMethod ?? "",
          num(o.vehicleCount),
          num(o.verifiedVehicleCount),
          num(o.actionRequiredVehicleCount),
          num(o.pendingReviewVehicleCount),
          num(o.bookingCount),
          num(o.rideCount),
          num(o.cancellationCount),
          num(o.cancellationRate),
          num(o.lifetimeFareTotal),
          num(o.lifetimeOwnerPayoutTotal),
          num(o.pendingPayoutTxnCount),
          num(o.pendingPayoutAmount),
          o.lastActivityAt || "",
          o.attentionNeeded ? "YES" : "NO",
        ]
          .map(csvEscape)
          .join(",")
      );
    });

    downloadTextFile(filename, lines.join("\n"));
  };

  const topBlock = useMemo(() => {
    if (!summary) return null;

    const totalOwners = num(summary.totalOwners).toLocaleString("en-IN");
    const linkedOwners = num(summary.linkedOwners).toLocaleString("en-IN");
    const unlinkedOwners = num(summary.unlinkedOwners).toLocaleString("en-IN");
    const totalVehicles = num(summary.totalVehicles).toLocaleString("en-IN");

    const bookings7d = num(summary.bookings7d).toLocaleString("en-IN");
    const rides7d = num(summary.rides7d).toLocaleString("en-IN");

    const totalBookingsAllTime = num(summary.totalBookingsAllTime).toLocaleString("en-IN");
    const cancelledBookingsAllTime = num(summary.cancelledBookingsAllTime).toLocaleString("en-IN");

    const totalPayout = moneyINR(summary.totalOwnerPayoutAllTime);

    const pendingPayoutAmt = moneyINR(summary.pendingPayoutAmount);
    const pendingPayoutHint = `${num(summary.pendingPayoutTxnCount).toLocaleString("en-IN")} pending txns`;

    const cancelRate = pct01(summary.cancellationRate, 0);
    const cancelHint = `${num(summary.cancelledBookingsAllTime).toLocaleString("en-IN")} / ${num(
      summary.totalBookingsAllTime
    ).toLocaleString("en-IN")} bookings`;

    const maxC = summary.maxCancelledOwner || null;
    const maxCancelValue = maxC
      ? `${num(maxC.cancelCount).toLocaleString("en-IN")} (${pct01(maxC.cancelRate, 0)})`
      : "—";
    const maxCancelHint = maxC ? labelOwner(maxC) : "—";

    return (
      <div className="space-y-4">
        {/* KPI grid (4 per row) — removed Total Fare + Active Rides, replaced with Lifetime Bookings + Lifetime Cancelled */}
      {/* KPI grid (6 per row, 2 rows on desktop) */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-3">
  <KpiCard title="Total owners" value={totalOwners} hint="All time" accent="blue" />
  <KpiCard title="Linked owners" value={linkedOwners} hint="Has vehicles" accent="green" />
  <KpiCard title="Unlinked owners" value={unlinkedOwners} hint="No vehicles" accent="amber" />
  <KpiCard title="Total vehicles" value={totalVehicles} hint="All time" accent="blue" />

  <KpiCard title="Bookings (7d)" value={bookings7d} hint="Created in last 7 days" accent="blue" />
  <KpiCard title="Rides (7d)" value={rides7d} hint="Created in last 7 days" accent="blue" />

  <KpiCard title="Bookings (lifetime)" value={totalBookingsAllTime} hint="All time" accent="blue" />
  <KpiCard title="Cancelled (lifetime)" value={cancelledBookingsAllTime} hint={`Rate: ${cancelRate}`} accent="red" />

  <KpiCard title="Owner payout (lifetime)" value={totalPayout} hint="SUCCESS payouts" accent="green" />
  <KpiCard title="Pending payout" value={pendingPayoutAmt} hint={pendingPayoutHint} accent="amber" />
  <KpiCard title="Cancellation rate" value={cancelRate} hint={cancelHint} accent="red" />
  <KpiCard title="Max cancellations" value={maxCancelValue} hint={maxCancelHint} accent="red" />
</div>


        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 2xl:col-span-4">
            <ChartCard title="Top owners by vehicles" subtitle="Most vehicles (all time)">
              <SimpleBarList items={(summary.topOwnersByVehicles || []).slice(0, 3)} valueKey="value" tone="blue" />
            </ChartCard>
          </div>

          <div className="col-span-12 2xl:col-span-4">
            <ChartCard title="Top owners by bookings" subtitle="Most bookings (all time)">
              <SimpleBarList items={(summary.topOwnersByBookings || []).slice(0, 3)} valueKey="value" tone="blue" />
            </ChartCard>
          </div>

          <div className="col-span-12 2xl:col-span-4">
            <ChartCard title="Top owners by rides" subtitle="Most rides (all time)">
              <SimpleBarList items={(summary.topOwnersByRides || []).slice(0, 3)} valueKey="value" tone="blue" />
            </ChartCard>
          </div>

          <div className="col-span-12 2xl:col-span-6">
            <ChartCard title="Top payout owners" subtitle="Highest SUCCESS payouts">
              <SimpleBarList
                items={(summary.topOwnersByPayout || []).slice(0, 3)}
                valueKey="amount"
                tone="green"
                rightRenderer={(x) => moneyINR(x?.amount)}
              />
            </ChartCard>
          </div>

          <div className="col-span-12 2xl:col-span-6">
            <ChartCard title="Most cancelled owners" subtitle="Cancellation count + % per owner">
              <div className="space-y-2">
                {(summary.topOwnersByCancellations || []).slice(0, 3).map((x, idx) => {
                  const cancels = num(x?.cancelCount);
                  const rate = num(x?.cancelRate); // 0..1
                  return (
                    <div key={idx} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-rose-50 text-[11px] font-bold text-rose-700 border border-rose-100">
                            {idx + 1}
                          </span>
                          <div className="text-sm font-semibold text-slate-900 truncate">{labelOwner(x)}</div>
                        </div>

                        <div className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                          {cancels.toLocaleString("en-IN")} • {pct01(rate, 0)}
                        </div>
                      </div>

                      <Meter value01={rate} tone="red" />
                    </div>
                  );
                })}

                {(summary.topOwnersByCancellations || []).length === 0 ? (
                  <div className="text-sm text-slate-500">No data.</div>
                ) : null}
              </div>
            </ChartCard>
          </div>
        </div>
      </div>
    );
  }, [summary]);

  return (
    <div className="min-w-0">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                Owners Directory
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Clean view: owners + linkage + payouts + cancellations + top performers.
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Chip tone="blue">READ ONLY</Chip>
              <Chip>GLOBAL</Chip>
            </div>
          </div>

          {/* KPI + charts */}
          <div className="mt-5">{topBlock}</div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/40">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 xl:flex xl:flex-wrap xl:items-end">
              <div className="w-full xl:min-w-[300px] xl:flex-1">
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Search</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Owner ID / phone / name / location"
                  className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400"
                />
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Payout status</div>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="ACTION_REQUIRED">Action required</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="DRAFT">Draft / missing</option>
                </select>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Link</div>
                <select
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="LINKED">Linked</option>
                  <option value="UNLINKED">Unlinked</option>
                </select>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Risk</div>
                <select
                  value={risk}
                  onChange={(e) => setRisk(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="ATTENTION">Attention only</option>
                </select>
              </div>
            </div>

            {/* right actions */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
              <div className="text-xs text-slate-500 whitespace-nowrap">
                Showing: <span className="text-slate-800">{loading ? "…" : items.length}</span> owners
              </div>

              <button
                onClick={exportCsv}
                disabled={loading || items.length === 0}
                className="inline-flex h-10 items-center justify-center rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-[1760px] w-full">
            <thead className="bg-white sticky top-0 z-10">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                <th className="px-6 py-3 w-[90px]">ID</th>
                <th className="px-6 py-3 w-[240px]">Name</th>
                <th className="px-6 py-3 w-[180px]">Phone</th>
                <th className="px-6 py-3 w-[240px]">Location</th>

                <th className="px-6 py-3 w-[220px]">Payout</th>

                <th className="px-6 py-3 w-[150px] text-right">Vehicles</th>
                <th className="px-6 py-3 w-[120px] text-right">Bookings</th>
                <th className="px-6 py-3 w-[120px] text-right">Rides</th>

                <th className="px-6 py-3 w-[170px] text-right">Lifetime fare</th>
                <th className="px-6 py-3 w-[190px] text-right">Lifetime payout</th>

                <th className="px-6 py-3 w-[160px] text-right">Pending payout</th>

                <th className="px-6 py-3 w-[160px] text-right">Cancels</th>
                <th className="px-6 py-3 w-[220px]">Last activity</th>
                <th className="px-6 py-3 w-[150px]">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 14 }).map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <div className="h-4 w-full rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : err ? (
                <tr>
                  <td colSpan={14} className="px-6 py-8">
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                      {err}
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-6 py-10 text-sm text-slate-600">
                    No owners found.
                  </td>
                </tr>
              ) : (
                items.map((o) => {
                  const oid = o.id;
                  const attn = !!o.attentionNeeded;

                  const payoutSt = o.payoutAccountStatus || "DRAFT";
                  const payoutMethod = cleanStr(o.payoutMethod);

                  const cancelRate = pct01(o.cancellationRate, 0);

                  return (
                    <tr key={oid} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap">{oid}</td>

                      <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis max-w-[240px]">
                        {o.name || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]">
                        {o.phone || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700 max-w-[240px] truncate" title={o.location || ""}>
                        {o.location || "—"}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Chip tone={payoutTone(payoutSt)}>{payoutLabel(payoutSt)}</Chip>
                          {payoutMethod ? <Chip tone="blue">{payoutMethod}</Chip> : <Chip>—</Chip>}
                          {attn ? <Chip tone="amber">ATTN</Chip> : <Chip>OK</Chip>}
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(o.vehicleCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(o.bookingCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(o.rideCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {moneyINR(o.lifetimeFareTotal)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {moneyINR(o.lifetimeOwnerPayoutTotal)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {moneyINR(o.pendingPayoutAmount)}{" "}
                        <span className="text-xs text-slate-500">({num(o.pendingPayoutTxnCount)})</span>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(o.cancellationCount).toLocaleString("en-IN")}{" "}
                        <span className="text-xs text-slate-500">({cancelRate})</span>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">{dtClean(o.lastActivityAt)}</td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <NavLink
                          to={`/admin/support/owners/${encodeURIComponent(String(oid))}`}
                          className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                        >
                          Open
                        </NavLink>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <PaginationFooter
          page={currentPage}
          size={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
          onSize={(n) => setSize(clamp(n, 10, 200))}
        />
      </div>
    </div>
  );
}
